<template>
  <HeaderBar />

  <ButtonBackContainer v-if="route.name !== 'swapDone'">
    <ButtonBack :fallback="{ name: 'upcomingHandovers' }">{{ t('back') }}</ButtonBack>

    <CVButton outline variant="error" @click="onCancelClicked">
      {{ t('cancelSwap') }}
      <XMarkIcon class="h-4 w-4" />
    </CVButton>
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <router-view />
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import { useBooking } from '@/queries/use-bookings';
import { useUiStore } from '@/stores/ui.store';
import { computed, watchEffect } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { Alert } from '@/services/alert.service';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';

const uiStore = useUiStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { data: booking } = useBooking(route.params.bookingId as string);
const swapToAllocation = computed(() => {
  return booking.value?.allocations.find((allocation) => allocation.id === route.query.swapToId);
});
const swapFromAllocation = computed(() => {
  return booking.value?.allocations.find((allocation) => allocation.id === route.query.swapFromId);
});

watchEffect(() => {
  if (booking.value) {
    const { customerData } = booking.value;
    uiStore.setHeaderTitle(
      t('carSwap'),
      `${customerData?.firstName ?? 'n/a'} ${customerData?.lastName ?? 'n/a'}`,
      `${swapFromAllocation.value?.car.vehicleType?.make.name} ${swapFromAllocation.value?.car.vehicleType?.model} → ${swapToAllocation.value?.car.vehicleType?.make.name} ${swapToAllocation.value?.car.vehicleType?.model}`,
    );
  } else {
    uiStore.setHeaderTitle(t('carSwap'));
  }
});

const onCancelClicked = async () => {
  const result = await Alert.fire({
    title: t('cancelSwap'),
    text: t('cancelSwapConfirmation'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('yes'),
    cancelButtonText: t('no'),
  });
  if (result.isConfirmed) {
    router.push({ name: 'upcomingHandovers' });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "carSwap": "Car swap",
    "back": "Back",
    "cancelSwapConfirmation": "Do you really want to cancel the swap?",
    "cancelSwap": "Cancel swap"
  },
  "de": {
    "carSwap": "Autotausch",
    "back": "Zurück",
    "cancelSwapConfirmation": "Möchtest du den Tausch wirklich abbrechen?",
    "cancelSwap": "Tausch abbrechen"
  },
  "es": {
    "carSwap": "Cambio de vehículo",
    "back": "Volver",
    "cancelSwapConfirmation": "¿Seguro que desea cancelar el cambio?",
    "cancelSwap": "Cancelar cambio"
  }
}
</i18n>
